/**
 * Shop and user context, plus the theme toggle and the fast-user-switch.
 *
 * Fast switching is in the header rather than buried in a profile screen on purpose: a shared
 * counter machine has to move between staff in seconds. If it takes longer than that, they share
 * one account and sales attribution stops meaning anything.
 */

import { useNavigate } from 'react-router';

import {
  dialog,
  resetApiState,
  selectTheme,
  switchedUser,
  themeChosen,
  type CurrentUser,
} from '@pos/shared';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { Chip, ShopMark } from '@/shared/ui';

export function ShellHeader({ user }: { user: CurrentUser | null }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useAppSelector(selectTheme);

  const switchUser = async () => {
    const ok = await dialog.confirm({
      title: 'Hand over to someone else?',
      // Named and concrete, per the dialog rules. Never "Are you sure?".
      body: `${user?.fullName ?? 'You'} will be signed out on this device. Anything in the cart is kept, and the offline catalogue stays — the next person only needs their password.`,
      confirmLabel: 'Switch user',
      cancelLabel: 'Stay signed in',
    });

    if (!ok) return;

    dispatch(switchedUser());
    dispatch(resetApiState());
    void navigate('/sign-in', { replace: true });
  };

  return (
    <header className="flex items-center gap-3 border-b border-line-subtle px-4 py-3" data-print="hide">
      {/* The shop's mark, not the product's. The name sits beside it, so the glyph is decorative. */}
      <ShopMark themeCode={user?.homeShopTheme} size={30} />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold leading-tight text-content-primary">
          {user?.homeShopName ?? '—'}
        </p>
        <p className="truncate text-xs leading-tight text-content-muted">
          {user?.fullName ?? 'Not signed in'} · {user?.roleName ?? ''}
        </p>
      </div>

      <button
        type="button"
        onClick={() => {
          // Three states cycled in the order a user expects: whatever the OS says, then the two
          // explicit choices.
          dispatch(themeChosen(theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system'));
        }}
        aria-label={`Theme: ${theme}. Change.`}
        className="flex min-h-compact min-w-compact items-center justify-center rounded-full text-content-secondary transition-colors duration-fast hover:bg-surface-sunken"
      >
        <ThemeIcon theme={theme} />
      </button>

      <button
        type="button"
        onClick={() => void switchUser()}
        className="flex min-h-compact min-w-compact items-center justify-center rounded-full transition-transform duration-instant active:scale-press"
        aria-label="Switch user"
      >
        <Chip tone="brand" dot={false}>
          {initials(user?.fullName)}
        </Chip>
      </button>
    </header>
  );
}

function initials(name: string | undefined): string {
  if (name === undefined || name.length === 0) return '–';

  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

  return `${first}${last}`.toUpperCase();
}

function ThemeIcon({ theme }: { theme: 'system' | 'light' | 'dark' }) {
  if (theme === 'system') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="4" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 20h8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }

  if (theme === 'light') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.7" />
        <path
          d="M12 3v2m0 14v2M3 12h2m14 0h2M5.6 5.6l1.4 1.4m10 10 1.4 1.4m0-12.8-1.4 1.4m-10 10-1.4 1.4"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}
